import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { PageSizeParams } from 'athena/components/mentoring-program/constants';
import axios from 'axios';
import { MentoringProgramGoalItem } from 'redux/schemas/models/mentoring-program-goal-items';

type MentorshipProgramParams = {
  mentorshipProgramId: number;
};

export type MentoringProgramGoalResponse = {
  enrollmentId: number;
  goalItem: MentoringProgramGoalItem;
};

export type CreateMentorshipProgramGoalItemParams = MentorshipProgramParams &
Omit<MentoringProgramGoalItem, 'id'> & { enrollmentId: number };

export const createMentorshipProgramGoalItem = createAsyncThunk<
MentoringProgramGoalResponse,
CreateMentorshipProgramGoalItemParams
>(
  'CREATE_MENTORSHIP_PROGRAM_GOAL_ITEM',
  async ({ mentorshipProgramId, description, status, enrollmentId }) => {
    const goal = {
      description,
      status,
    };
    const response = await axios.post(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals`,
      {
        goal,
      },
    );
    const goalItem = response.data.result as MentoringProgramGoalItem;
    return { enrollmentId, goalItem };
  },
);

export type GetMentorshipProgramGoalItemsParams = MentorshipProgramParams &
PageSizeParams & { page: number; enrollmentId: number };

export type GetMentorshipProgramGoalItemsResponse = {
  mentorshipProgramEnrollmentGoals: MentoringProgramGoalItem[];
  totalCount: number;
};
export const getMentorshipProgramGoalItems = createAsyncThunk<
MentoringProgramGoalItem[],
GetMentorshipProgramGoalItemsParams
>(
  'GET_MENTORSHIP_PROGRAM_GOAL_ITEMS',
  async ({
    mentorshipProgramId,
    page,
    pageSize,
    enrollmentId,
  }, thunkAPI) => {
    const params = {
      page,
      page_size: pageSize,
      mentorship_program_enrollment_id: enrollmentId || undefined,
    };
    const response = await axios.get(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals`,
      {
        params,
      },
    );
    const { mentorshipProgramEnrollmentGoals, totalCount } = response.data.result as GetMentorshipProgramGoalItemsResponse;
    thunkAPI.dispatch(setMentorshipProgramGoalsTotalCount({ enrollmentId, totalCount }));
    return mentorshipProgramEnrollmentGoals;
  },
);

export type UpdateMentorshipProgramGoalItemParams = MentorshipProgramParams & {
  id: MentoringProgramGoalItem['id'];
  enrollmentId: number;
} & Partial<Omit<MentoringProgramGoalItem, 'id'>>;

export const updateMentorshipProgramGoalItem = createAsyncThunk<
MentoringProgramGoalResponse,
UpdateMentorshipProgramGoalItemParams
>(
  'UPDATE_MENTORSHIP_PROGRAM_GOAL_ITEM',
  async ({ mentorshipProgramId, id, description, status, enrollmentId }) => {
    const goal = {
      description: description || undefined,
      status: status || undefined,
    };
    const response = await axios.put(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals/${id}.json`,
      {
        goal,
      },
    );
    const goalItem = response.data.result as MentoringProgramGoalItem;
    return { enrollmentId, goalItem };
  },
);

type DeleteMentorshipProgramGoalItemParams = MentorshipProgramParams & {
  id: MentoringProgramGoalItem['id'];
  enrollmentId: number;
};

export const deleteMentorshipProgramGoalItem = createAsyncThunk<
boolean,
DeleteMentorshipProgramGoalItemParams
>(
  'DELETE_MENTORSHIP_PROGRAM_GOAL_ITEM',
  async ({ mentorshipProgramId, id }) => {
    const response = await axios.delete(
      `/mentorship_programs/${mentorshipProgramId}/mentorship_program_enrollment_goals/${id}.json`,
    );
    const isGoalItemDeleted = response.data.result;
    return isGoalItemDeleted;
  },
);

export const setMentorshipProgramGoalsTotalCount = createAction<{
  enrollmentId: number;
  totalCount: number;
}>('SET_MENTORSHIP_PROGRAM_GOALS_TOTAL_COUNT');
